import React, { useCallback, useState } from "react";
import { Typography } from "@mui/material";
import { submitForm } from "../../api/forms.api";
import DynamicForm from "../DynamicForm/DynamicForm";
import { FormsProps } from "../../types/forms";
import { useMessage } from "../../hooks/useMessage";
import { useEvents } from "../../hooks/useEvents";
import { useError } from "../../context/error-context";

const AddSingleRowForm = ({
  formId,
  formName,
  formType,
  formAllFields,
  skin,
  onSubmitSuccess,
}: FormsProps) => {
  const { showMessage, MessageContainer } = useMessage();
  const [submitting, setSubmitting] = useState(false);
  const { emit } = useEvents();
  const { showError } = useError();

  const submitFormHandler = async (values: { [key: string]: any }) => {
  if (!formId) {
    return;
  }

  const formData: {
    form_id: number;
    data: {
      id: string;
      value: any;
    }[];
  } = {
    form_id: +formId,
    data: [],
  };

  Object.keys(values).forEach((key: string) => {
    formData.data.push({
      id: key,
      value: values[key],
    });
  });

  if (formData.data.length === 0) {
    return;
  }

  setSubmitting(true);

  try {
    const response = await submitForm(formData);

    const popupValues = formData.data.reduce<Record<string, any>>(
      (result, item) => {
        result[item.id] = item.value;

        return result;
      },
      {},
    );

    const popupMessage = {
        type: "popup-form-saved",
        payload: {
            formId: response?.submission?.form_id ?? formId,
            rowId:
            response?.row_id ??
            response?.response_id ??
            response?.rowid ??
            "",
            values: popupValues,
        },
    };

    if (window.parent !== window) {
    window.parent.postMessage(popupMessage, "*");
    }

    showMessage("Your data has been saved!", "success");

    const payload: {
      [key: string]: any;
    } = {
      __formId: formId,
    };

    formAllFields.forEach((field) => {
      const value = formData.data.find((row) => row.id === field.id);

      if (value !== undefined) {
        payload[field.id] = value;
      }
    });

    emit(`REFRESH_FORMS_${formId}`, payload);
    emit("REFRESH_IFRAMES", payload);
    onSubmitSuccess?.(payload);
  } catch (err: any) {
    showError({
      code: err.response?.status || 500,
      message: "API Error",
      description: "Failed to fetch data",
      showRefresh: true,
    });
  } finally {
    setSubmitting(false);
  }
};

  const resetFormEmittedData = useCallback(() => {
    emit(`REFRESH_FORMS_${formId}`, {});
  }, [emit, formId]);

  return (
    <div style={{ margin: 16 }}>
      <div>
        {skin === "skin1" && (
          <Typography variant="h4" gutterBottom>
            {formName || "Fill in the form"}
          </Typography>
        )}
      </div>

      <DynamicForm
        formId={formId}
        formName={formName}
        formType={formType}
        config={{ fields: formAllFields }}
        onSubmit={submitFormHandler}
        apiSubmitting={submitting}
        skin={skin}
        shouldSeparateLeaves={skin === "skin3"}
        resetFormEmittedData={resetFormEmittedData}
      />

      <MessageContainer />
    </div>
  );
};

export default AddSingleRowForm;